import React, { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Pencil, ClipboardList, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatAddress, formatDateTime } from "@/lib/format";
import { woStatusTone } from "@/lib/wo";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusChip, statusTone } from "@/components/common/StatusChip";
import { EmptyState } from "@/components/common/EmptyState";
import { PropertyDialog } from "@/features/properties/PropertyDialog";
import { ClientSummaryDialog } from "@/features/properties/ClientSummaryDialog";
import { UnitsTab } from "@/features/properties/UnitsTab";
import { BuildingAliasesTab } from "@/features/properties/BuildingAliasesTab";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";

const Row = ({ label, value, testId }) => (
  <div className="flex items-start justify-between gap-6 border-b border-line py-2 last:border-0">
    <span className="text-[12px] text-ink-tertiary">{label}</span>
    <span data-testid={testId} className="max-w-[60%] text-right text-[13px] text-ink">
      {value || "—"}
    </span>
  </div>
);

export default function PropertyDetailPage() {
  const { t } = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [params] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["property", id],
    queryFn: async () => (await api.get(`/properties/${id}`)).data,
  });

  const property = data?.property;

  return (
    <>
      <PageHeader
        title={property?.nombre || t("app.loading")}
        subtitle={property ? formatAddress(property.direccion) : ""}
        testId="property-detail-header"
        breadcrumb={
          <button
            data-testid="property-back"
            onClick={() => navigate("/edificios")}
            className="mb-1 flex items-center gap-1 text-[12px] text-ink-tertiary hover:text-brand"
          >
            <ArrowLeft className="h-3 w-3" /> {t("properties.title")}
          </button>
        }
        actions={
          <>
            {property && (
              <>
                <StatusChip tone="blue" label={property.brand === "bb" ? "B&B" : "Betos"} />
                <StatusChip tone={statusTone(property.status)} label={t(`status.${property.status}`)} />
              </>
            )}
            {can("messaging", "create") && property && (
              <Button
                data-testid="property-send-summary"
                onClick={() => setSummaryOpen(true)}
                variant="outline"
                className="h-8 rounded-[6px] text-[13px]"
              >
                <Send className="mr-1.5 h-3.5 w-3.5" />
                {t("summary.send_now")}
              </Button>
            )}
            {can("properties", "update") && (
              <Button
                data-testid="property-edit-button"
                onClick={() => setEditOpen(true)}
                variant="outline"
                className="h-8 rounded-[6px] text-[13px]"
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                {t("app.edit")}
              </Button>
            )}
          </>
        }
      />

      <div className="flex-1 overflow-y-auto p-6" data-testid="property-detail-page">
        {isLoading ? (
          <Skeleton className="h-[320px] w-full rounded-[8px]" />
        ) : (
          <Tabs defaultValue={params.get("tab") === "units" ? "units" : "general"}>
            <TabsList className="mb-4 h-9 rounded-[6px] bg-white">
              <TabsTrigger value="general" data-testid="tab-property-general" className="text-[13px]">
                {t("properties.tabs.general")}
              </TabsTrigger>
              <TabsTrigger value="units" data-testid="tab-property-units" className="text-[13px]">
                {t("properties.tabs.units")} ({property?.unidades_count ?? 0})
              </TabsTrigger>
              <TabsTrigger value="work_orders" data-testid="tab-property-work-orders" className="text-[13px]">
                {t("properties.tabs.work_orders")}
              </TabsTrigger>
              <TabsTrigger value="access" data-testid="tab-property-access" className="text-[13px]">
                {t("properties.tabs.access")}
              </TabsTrigger>
              {can("intake", "manage") && (
                <TabsTrigger value="intake" data-testid="tab-property-intake" className="text-[13px]">
                  {t("properties.tabs.intake")}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="general">
              <div className="grid gap-4 lg:grid-cols-2">
                <section className="rounded-[8px] border border-line bg-white p-4">
                  <h2 className="mb-2 text-[13px] font-semibold text-ink">
                    {t("properties.tabs.general")}
                  </h2>
                  <Row label={t("app.name")} value={property?.nombre} testId="property-name" />
                  <Row label={t("properties.company")} value={property?.company_nombre} />
                  <Row label={t("app.address")} value={formatAddress(property?.direccion)} testId="property-address" />
                  <Row label={t("properties.declared_units")} value={property?.cantidad_unidades} />
                  <Row label={t("properties.units_count")} value={property?.unidades_count} />
                  <Row label={t("properties.open_orders")} value={property?.ordenes_abiertas} />
                  <Row label={t("properties.overdue_orders")} value={property?.ordenes_vencidas} />
                  <Row label={t("app.notes")} value={property?.notas} />
                </section>
                <section className="rounded-[8px] border border-line bg-white p-4">
                  <h2 className="mb-2 text-[13px] font-semibold text-ink">
                    {t("properties.site_manager")}
                  </h2>
                  <Row label={t("app.name")} value={property?.site_manager?.nombre} />
                  <Row label={t("app.email")} value={property?.site_manager?.email} />
                  <Row label={t("app.phone")} value={property?.site_manager?.telefono} />
                  {"email_facturacion" in (property || {}) && (
                    <>
                      <Row label={t("properties.billing_email")} value={property?.email_facturacion} />
                      <Row label={t("properties.billing_phone")} value={property?.telefono_facturacion} />
                    </>
                  )}
                  <Row
                    label={t("properties.notify_channel")}
                    value={property?.notificacion_cliente?.canal}
                  />
                  <Row
                    label={t("properties.notify_interval")}
                    value={property?.notificacion_cliente?.intervalo_dias}
                  />
                </section>
              </div>
            </TabsContent>

            <TabsContent value="units">
              <UnitsTab propertyId={id} />
            </TabsContent>

            <TabsContent value="work_orders">
              <div className="overflow-hidden rounded-[8px] border border-line bg-white">
                {data?.work_orders?.length ? (
                  <table className="w-full text-[13px]" data-testid="property-work-orders-table">
                    <thead className="bg-surface-sunken">
                      <tr className="border-b border-line text-[12px] uppercase text-ink-tertiary">
                        <th className="px-3 py-2 text-left">Código</th>
                        <th className="px-3 py-2 text-left">Título</th>
                        <th className="px-3 py-2 text-left">{t("app.status")}</th>
                        <th className="px-3 py-2 text-left">Creada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.work_orders.map((w) => (
                        <tr
                          key={w.id}
                          data-testid={`property-wo-${w.code}`}
                          onClick={() => navigate(`/ordenes/${w.id}`)}
                          className="h-[44px] cursor-pointer border-b border-line last:border-0 hover:bg-surface-sunken"
                        >
                          <td className="px-3 font-medium">{w.code}</td>
                          <td className="px-3">{w.titulo}</td>
                          <td className="px-3">
                            <StatusChip tone={woStatusTone(w.status)} label={t(`woStatus.${w.status}`)} />
                          </td>
                          <td className="px-3 text-ink-tertiary">{formatDateTime(w.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <EmptyState
                    icon={ClipboardList}
                    testId="property-work-orders-empty"
                    title={t("soon.title")}
                    description={t("soon.work_orders")}
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent value="access">
              <section className="rounded-[8px] border border-line bg-white p-4">
                <h2 className="mb-2 text-[13px] font-semibold text-ink">
                  {t("properties.access_notes")}
                </h2>
                <p
                  data-testid="property-access-notes"
                  className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-ink"
                >
                  {property?.notas_acceso || "—"}
                </p>
              </section>
            </TabsContent>

            {can("intake", "manage") && (
              <TabsContent value="intake">
                <BuildingAliasesTab propertyId={id} />
              </TabsContent>
            )}
          </Tabs>
        )}
      </div>

      <PropertyDialog open={editOpen} onOpenChange={setEditOpen} property={property} />
      <ClientSummaryDialog open={summaryOpen} onOpenChange={setSummaryOpen} property={property} />
    </>
  );
}
